import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { ArrowLeft } from "react-feather";
import { displayToast } from "../../utils/Toast";
import { useErrorHandler } from "../../services/ErrorHandler";
import Edit from "../../assets/svgIcons/FormEdit.svg";
import { capitalizeFirstLetterOfEachWord } from "../../utils/Formatter";
import { getFieldMaster } from "../../apis/fieldMaster";
import {
  insertMasterTransaction,
  fetchTransactionById,
  editMasterTransaction,
  insertProductMaster,
  finaliseProductMaster,
  editProductMaster,
  fetchTransactionPopupData,
  deleteTransactionByCode,
  fetchBomByProduct,
} from "../../apis/transaction";
import Delete from "../../assets/svgIcons/FormDelete.svg";
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { useSalesFormStoreApi } from "../../services/useSalesFormStore";
import { usePermissions } from "../../hooks/usePermissions";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import { uploadFile } from "../../utils/uploadFile";
import DatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useMode } from "../../context/ModeContext";
import {
  Upload,
  FilePlus,
  Package,
  ClipboardCheck,
  ClipboardList,
} from "lucide-react";
import "../../styles/components/MasterDataModal.css";
import ProcessCardPopup from "../ProcessCardPopup";

const DateCalendar = ({ children, onClear, className, ...props }) => {
  const [dropdownRow, setDropdownRow] = useState(null);
  const calendarRef = useCallback((node) => {
    setDropdownRow(
      node?.querySelector(".react-datepicker__header__dropdown") ?? null,
    );
  }, []);

  return (
    <div ref={calendarRef}>
      <CalendarContainer
        {...props}
        className={`${className || ""} clearable-datepicker-calendar`}
      >
        {children}
      </CalendarContainer>
      {dropdownRow &&
        onClear &&
        createPortal(
          <button
            type="button"
            className="react-datepicker__month-read-view calendar-clear-button"
            onClick={onClear}
          >
            Clear
          </button>,
          dropdownRow,
        )}
    </div>
  );
};

const ProductMaster = ({
  transactionCode: propTransactionCode,
  heading = "",
  selectedDetail = "",
  modeOfEdit = "",
  onClose,
  extraForms,
  newEntryTranCode,
  customerType = "",
  isPopupMode = false,
  showAllFields = false,
  onProductCreated,
  finaliseMode = false,
  prefillValues = null,
  finaliseContext = null,
  onFinalised,
}) => {
  const location = useLocation();
  const salesFormStore = useSalesFormStoreApi();
  const { setMode } = useMode();
  const { tab: urlTab, category, mode } = useParams();
  const navigate = useNavigate();
  const { handleErrorResponse } = useErrorHandler();
  const drawingInputRef = useRef(null);
  const qcInputRef = useRef(null);
  const inputRefs = useRef({});

  const tab =
    urlTab ||
    (window.location.pathname.startsWith("/master") ? "master" : undefined);

  const transactionCode = (() => {
    if (propTransactionCode) {
      return propTransactionCode;
    }
    if (location.state?.transactionCode) {
      return location.state.transactionCode;
    }
    if (heading === "otherDetailsModal") {
      return location.state?.transactionCode;
    }
    return null;
  })();

  const displayCategory =
    transactionCode === "CM"
      ? "customer"
      : transactionCode === "VM"
        ? "vendor"
        : transactionCode === "PM"
          ? "new Product"
          : category;

  const transId = location.state?.transId;
  const colorCode = location.state?.colorCode;
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [currentMode, setCurrentMode] = useState(mode);
  useAutoRefresh(() => fetchTransactionData(), {
    enabled: currentMode === "view" && Boolean(transId),
  });
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [openDropdownField, setOpenDropdownField] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const rights = usePermissions(transactionCode);

  const [drawingUrl, setDrawingUrl] = useState("");
  const [inspectionFormatUrl, setInspectionFormatUrl] = useState("");
  const [showProcessCardPopup, setShowProcessCardPopup] = useState(false);
  const [productBomView, setProductBomView] = useState(null);

  useEffect(() => {
    let isActive = true;

    const loadProductBom = async () => {
      if (transactionCode !== "PM" || mode !== "view" || !transId) {
        setProductBomView(null);
        return;
      }
      setProductBomView({ loading: true });
      try {
        const response = await fetchBomByProduct(Number(transId));
        if (!isActive) return;
        if (response?.head_data) {
          setProductBomView({ loading: false, data: response });
        } else {
          setProductBomView({
            loading: false,
            error: response?.description || "No BOM created yet",
          });
        }
      } catch (error) {
        if (isActive) {
          setProductBomView({ loading: false, error: "No BOM created yet" });
        }
      }
    };

    loadProductBom();
    return () => {
      isActive = false;
    };
  }, [transactionCode, mode, transId]);

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    try {
      const response = await uploadFile(file, type);
      const fileUrl = response?.url || "";

      if (type === "drawing") {
        setDrawingUrl(fileUrl);
      } else if (type === "inspection_format") {
        setInspectionFormatUrl(fileUrl);
      }
    } catch (err) {
      displayToast("Upload failed", err);
    }
  };

  const detailButtons =
    extraForms?.length > 0
      ? extraForms.map((form) =>
          capitalizeFirstLetterOfEachWord(form.label || form.title || ""),
        )
      : [];

  useEffect(() => {
    if (heading === "otherDetailsModal" && Array.isArray(selectedDetail)) {
      const { otherDetailsData } = salesFormStore.getState();

      const initialData = selectedDetail.reduce((acc, field) => {
        const existingData = otherDetailsData.find(
          (detail) => detail[field.field_name] !== undefined,
        );
        acc[field.field_name] = existingData
          ? existingData[field.field_name]
          : "";
        return acc;
      }, {});

      setFormData(initialData);
    }
  }, [selectedDetail, heading, salesFormStore]);

  useEffect(() => {
    if (currentMode === "add") {
      setMode("add");
    } else if (currentMode === "edit") {
      setMode("edit");
    } else {
      setMode("view");
    }
  }, [setMode, currentMode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".modal-dropdown-container")) {
        setOpenDropdownField(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      if (heading === "otherDetailsModal") {
        setFields(selectedDetail);
      } else {
        await fetchFieldMaster();
        if (mode === "view" && transId) {
          await fetchTransactionData();
        }
      }
    };
    initializeData();
  }, [transactionCode, transId, heading]);

  const fetchTransactionData = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await fetchTransactionById(transactionCode, transId);
      if (response.description) {
        displayToast(response.description, "error");
      } else {
        if (response.head_data) {
          setFormData(response.head_data);

          const { drawing_url, inspection_format_url } = response.head_data;
          drawing_url && setDrawingUrl(drawing_url);
          inspection_format_url &&
            setInspectionFormatUrl(inspection_format_url);
        }
      }
    } catch (error) {
      displayToast("Failed to fetch transaction data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  function normalizePathValue(value) {
    if (!value) return "";

    const decoded = decodeURIComponent(value.trim().toLowerCase());

    const exceptions = ["sales"];

    let normalizedValue = decoded;

    if (!exceptions.includes(decoded)) {
      normalizedValue = decoded.endsWith("s") ? decoded.slice(0, -1) : decoded;
    }

    normalizedValue = normalizedValue.replace(/\s+/g, "");
    const finalValue =
      normalizedValue.charAt(0).toUpperCase() +
      normalizedValue.slice(1).toLowerCase();

    return finalValue;
  }

  const fetchFieldMaster = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getFieldMaster({
        transactionCode: transactionCode,
      });
      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        let validFields = (response.items?.head_fields || [])
          .filter((field) => !field.is_internal_field)
          .sort((a, b) => a.order_no - b.order_no);

        if (isPopupMode && !finaliseMode && !showAllFields) {
          validFields = validFields.filter((field) => field.is_required);
        }

        setFields(validFields);

        if (mode !== "view") {
          const pathParts = window.location.pathname.split("/").filter(Boolean);
          const thirdItem = pathParts[2] || null;

          const decodedValue = thirdItem ? normalizePathValue(thirdItem) : "";

          const initialFormData = validFields.reduce((acc, field) => {
            acc[field.field_name] = "";
            return acc;
          }, {});

          if (decodedValue) {
            initialFormData.type = decodedValue;
          }
          if (prefillValues) {
            Object.entries(prefillValues).forEach(([key, value]) => {
              if (
                value !== undefined &&
                value !== null &&
                value !== "" &&
                Object.prototype.hasOwnProperty.call(initialFormData, key)
              ) {
                initialFormData[key] = value;
              }
            });
          }
          setFormData(initialFormData);
        }
      }
    } catch (error) {
      displayToast("Failed to fetch field master data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (value === "Add New +") {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsButtonLoading(true);

    if ((currentMode || modeOfEdit) === "add" && !rights.can_add) {
      displayToast(
        `You dont have permission to add ${displayCategory}`,
        "error",
      );
      setIsButtonLoading(false);
      return;
    }

    if (currentMode === "edit" && !rights.can_edit) {
      displayToast(
        `You dont have permission to edit ${displayCategory}`,
        "error",
      );
      setIsButtonLoading(false);
      return;
    }

    const { addOtherDetails, otherDetailsData } = salesFormStore.getState();

    if (heading === "otherDetailsModal" && modeOfEdit === "add") {
      let detailsMap = new Map();

      otherDetailsData.forEach((item) => {
        const key = Object.keys(item)[0];
        detailsMap.set(key, item);
      });

      Object.keys(formData).forEach((key) => {
        detailsMap.set(key, { [key]: formData[key] });
      });

      const updatedDetails = Array.from(detailsMap.values());

      addOtherDetails(updatedDetails);
      displayToast("Other details saved successfully", "success");
      onClose();
      setIsButtonLoading(false);
      return;
    }

    const isAddMode = (currentMode || modeOfEdit) === "add";

    if (finaliseMode) {
      const missingField = fields.find(
        (field) =>
          field.is_required && !String(formData[field.field_name] ?? "").trim(),
      );
      if (missingField) {
        displayToast(
          `${missingField.title || missingField.field_name} is required`,
          "error",
        );
        setIsButtonLoading(false);
        return;
      }

      const organisation = JSON.parse(localStorage.getItem("organisation"));
      const submissionData = {
        head_data: {
          organisation_id: organisation?.id,
          tran_no: "",
          ...formData,
          drawing_url: drawingUrl,
          inspection_format_url: inspectionFormatUrl,
        },
        co_tran_code: finaliseContext?.coTranCode,
        co_tran_id: finaliseContext?.coTranId,
        item_row_id: finaliseContext?.itemRowId,
      };

      try {
        const response = await finaliseProductMaster(submissionData);
        if (response?.product_id) {
          displayToast("Product finalised and added to master", "success");
          onFinalised?.(response.product_id);
          onClose?.();
        } else {
          const message =
            response?.error?.description ||
            response?.description ||
            "Failed to finalise product";
          handleErrorResponse(message);
          displayToast(message, "error");
        }
      } catch (error) {
        displayToast("Failed to finalise product", "error");
      } finally {
        setIsButtonLoading(false);
      }
      return;
    }

    if (isAddMode) {
      const organisation = JSON.parse(localStorage.getItem("organisation"));
      const organisationId = organisation?.id;
      if (!organisationId) {
        displayToast("Organisation ID not found", "error");
        setIsButtonLoading(false);
        return;
      }

      const submissionData = {
        head_data: {
          organisation_id: organisationId,
          tran_no: "",
          ...formData,
          ...otherDetailsData,
          drawing_url: drawingUrl,
          inspection_format_url: inspectionFormatUrl,
        },
      };

      try {
        let response;

        if (transactionCode === "PM") {
          response = await insertProductMaster(submissionData);
        } else {
          response = await insertMasterTransaction(
            submissionData,
            transactionCode,
          );
        }

        if (response?.head_data) {
          displayToast("Transaction added successfully", "success");

          if (isPopupMode) {
            onProductCreated?.(response.head_data);
            onClose();
          } else if (modeOfEdit !== "add") {
            if (category) {
              sessionStorage.setItem(`clearCache_master_${category}`, "true");
            }
            navigate(-1);
          } else {
            navigate(`/${tab}/${category}/transactions`, {
              state: {
                company: response.head_data,
                transactionCode: newEntryTranCode,
                customerType: customerType,
              },
            });
            onClose();
          }
        } else if (response?.description) {
          handleErrorResponse(response.description);
          displayToast(response.description, "error");
        }
      } catch (error) {
        displayToast("Error adding transaction", "error");
      } finally {
        setIsButtonLoading(false);
      }
    } else if (currentMode === "edit" && transId) {
      try {
        const updatedData = {
          tran_id: transId,
          head_data: {
            ...formData,
            ...otherDetailsData,
            drawing_url: drawingUrl,
            inspection_format_url: inspectionFormatUrl,
          },
          item_data: [],
        };

        let response;

        if (transactionCode === "PM") {
          response = await editProductMaster(updatedData);
        } else {
          response = await editMasterTransaction(transactionCode, updatedData);
        }

        if (response?.head_data) {
          displayToast("Transaction updated successfully", "success");
          if (category) {
            sessionStorage.setItem(`clearCache_master_${category}`, "true");
          }
          navigate(-1);
        } else if (response.description) {
          handleErrorResponse(response.description);
          displayToast(response.description, "error");
        }
      } catch (error) {
        displayToast("Error updating transaction", "error");
      } finally {
        setIsButtonLoading(false);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteTransactionByCode(transactionCode, transId);

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        displayToast("Transaction deleted successfully", "success");
        if (category) {
          sessionStorage.setItem(`clearCache_master_${category}`, "true");
        }
        navigate(-1);
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const handleDeleteClick = () => {
    if (!rights.can_delete) {
      displayToast(
        `You don't have permission to delete ${displayCategory}`,
        "error",
      );
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const toggleEditMode = () => {
    if (!rights.can_edit) {
      displayToast(
        `You dont have permission to edit ${displayCategory}`,
        "error",
      );
      return;
    }
    setCurrentMode("edit");
  };

  const handleCloseEditMode = () => {
    setCurrentMode("view");
    if (mode === "view" && transId) {
      fetchTransactionData();
    }
  };

  useEffect(() => {
    const fetchAllDropdownOptions = async () => {
      const queryFields = fields.filter(
        (field) =>
          field.data_type === "DROPDOWN" && field.is_query && field.db_query,
      );

      const newOptions = {};

      for (const field of queryFields) {
        try {
          const response = await fetchTransactionPopupData(field.db_query);
          if (response?.items?.length > 0) {
            newOptions[field.field_name] = response.items?.map((item) =>
              typeof item === "object" ? item.category || "" : item,
            );
          } else {
            displayToast(response.message, "error");
          }
        } catch (error) {
          console.error(
            `Error fetching options for ${field.field_name}:`,
            error,
          );
        }
      }

      setDropdownOptions((prev) => ({ ...prev, ...newOptions }));
    };

    fetchAllDropdownOptions();
  }, [fields]);

  const renderField = (field) => {
    if (!field) return null;

    const fieldValue = formData[field.field_name] || "";

    const optionsToRender = field.is_query
      ? dropdownOptions[field.field_name] || []
      : field.options
          ?.split(",")
          .map((opt) => opt.trim())
          .map(
            (opt) => opt.charAt(0).toUpperCase() + opt.slice(1).toLowerCase(),
          );
    if (field.field_type === "DATE") {
      return (
        <div className="master-data-modal-form-field" key={field.field_name}>
          <label className="master-data-modal-label">
            {field.title}
            {field.is_required && <span className="required-mark">*</span>}
          </label>
          <DatePicker
            selected={fieldValue ? new Date(fieldValue) : null}
            onChange={(date) =>
              setFormData((prev) => ({
                ...prev,
                [field.field_name]: date,
              }))
            }
            dateFormat="dd-MM-yyyy"
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={26}
            dropdownMode="scroll"
            calendarContainer={(props) => (
              <DateCalendar
                {...props}
                onClear={
                  currentMode === "view"
                    ? undefined
                    : () =>
                        setFormData((prev) => ({
                          ...prev,
                          [field.field_name]: null,
                        }))
                }
              />
            )}
            className="master-data-modal-input"
            placeholderText={`Select ${field.title.toLowerCase()}`}
            disabled={currentMode === "view"}
            wrapperClassName="master-data-modal-datepicker-wrapper"
            popperProps={{ strategy: "fixed" }}
            portalId="root-portal"
          />
        </div>
      );
    }
    const isMultiLineField =
      field.field_type?.toUpperCase() === "TEXT" ||
      (field.field_length &&
        field.field_length > 255 &&
        !field.options &&
        !field.is_query);

    if (isMultiLineField) {
      const adjustHeight = (textarea) => {
        if (textarea) {
          textarea.style.height = "24px";
          const newHeight = Math.min(textarea.scrollHeight, 72);
          textarea.style.height = `${newHeight}px`;
          textarea.style.overflowY =
            textarea.scrollHeight > 72 ? "auto" : "hidden";
        }
      };

      return (
        <div className="master-data-modal-form-field" key={field.field_name}>
          <label className="master-data-modal-label">
            {field.title}
            {field.is_required && <span className="required-mark">*</span>}
          </label>
          <textarea
            name={field.field_name}
            value={fieldValue || ""}
            onChange={(e) => {
              handleInputChange(e);
              if (currentMode !== "view") adjustHeight(e.target);
            }}
            className={`master-type-text-input ${
              currentMode === "view" ? "non-editable" : ""
            }`}
            disabled={currentMode === "view"}
            placeholder={`Enter ${field.title || field.field_name}`}
            ref={(el) => {
              inputRefs.current[field.field_name] = { current: el };
              if (el) adjustHeight(el);
            }}
            rows={1}
            maxLength={field.field_length}
            required={field.is_required}
          />
        </div>
      );
    }
    return (
      <div className="master-data-modal-form-field" key={field.field_name}>
        <label className="master-data-modal-label">
          {field.title}
          {field.is_required && <span className="required-mark">*</span>}
        </label>
        {field.options ? (
          <div className="modal-dropdown-container">
            <div
              className={`customer-data-modal-input ${
                currentMode === "view"
                  ? "non-editable"
                  : "master-data-modal-select-wrapper"
              } ${
                openDropdownField === field.field_name
                  ? "dropdown-open-highlight"
                  : ""
              }`}
              onClick={() =>
                currentMode !== "view" && setOpenDropdownField(field.field_name)
              }
            >
              <span className="modal-dropdown-text">
                {fieldValue || `Select ${field.title}`}
              </span>
              <span className="dropdown-icon">▼</span>
            </div>

            {openDropdownField === field.field_name &&
              currentMode !== "view" && (
                <div className="primary-dropdown-options">
                  {Array.isArray(optionsToRender) &&
                    optionsToRender.map((option, index) => {
                      const isSelected = formData[field.field_name] === option;
                      return (
                        <div
                          key={index}
                          className={`primary-dropdown-option ${
                            isSelected ? "selected-option" : ""
                          }`}
                          onClick={() => {
                            handleInputChange({
                              target: {
                                name: field.field_name,
                                value: option,
                              },
                            });
                            setOpenDropdownField(null);
                          }}
                        >
                          {option}
                        </div>
                      );
                    })}
                </div>
              )}
          </div>
        ) : field.field_type === "BOOLEAN" ? (
          <input
            type="checkbox"
            name={field.field_name}
            className="master-data-modal-checkbox"
            checked={!!fieldValue}
            onChange={(e) =>
              handleInputChange({
                target: {
                  name: field.field_name,
                  value: e.target.checked,
                },
              })
            }
            disabled={currentMode === "view"}
          />
        ) : (
          <input
            type={field.data_type === "int" ? "number" : "text"}
            name={field.field_name}
            className={`master-data-modal-input ${
              currentMode === "view" ? "non-editable" : ""
            }`}
            placeholder={`Enter ${field.title.toLowerCase()}`}
            value={fieldValue}
            onChange={handleInputChange}
            readOnly={currentMode === "view"}
            maxLength={field.field_length}
            required={field.is_required}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="master-data-modal-container">
        <Loader />
      </div>
    );
  }

  const handleProcessCardSaved = () => {
    if (typeof fetchTransactionData === "function") {
      fetchTransactionData();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="master-data-modal-container">
      <div
        className="master-data-modal-header"
        style={{
          backgroundColor: colorCode ? `#${colorCode}` : "#fff",
        }}
      >
        <div className="master-data-modal-title-container">
          <h2 className="master-data-modal-title">
            {heading !== "otherDetailsModal"
              ? `${
                  isPopupMode && !showAllFields ? "Quick Add " : ""
                }${capitalizeFirstLetterOfEachWord(
                  currentMode,
                )} ${capitalizeFirstLetterOfEachWord(displayCategory)}`
              : `${capitalizeFirstLetterOfEachWord(
                  modeOfEdit,
                )} ${capitalizeFirstLetterOfEachWord(selectedDetail.title)}`}
          </h2>
          {currentMode === "view" ? (
            <>
              <img
                src={Edit}
                onClick={toggleEditMode}
                className="sales-form-icons"
                alt="Edit"
                style={{
                  cursor: rights.can_edit ? "pointer" : "not-allowed",
                  opacity: rights.can_edit ? 1 : 0.5,
                }}
                title={rights.can_edit ? "Edit" : "No permission to edit"}
              />
              <img
                src={Delete}
                className="sales-form-icons"
                alt="Delete"
                onClick={handleDeleteClick}
                style={{
                  cursor: rights.can_delete ? "pointer" : "not-allowed",
                  opacity: rights.can_delete ? 1 : 0.5,
                  marginLeft: 12,
                }}
                title={
                  rights.can_delete
                    ? "Delete Transaction"
                    : "No permission to delete"
                }
              />
              {window.location.pathname.startsWith("/master") && (
                <button className="back-button-modal" onClick={onClose}>
                  <ArrowLeft size={25} />
                </button>
              )}
            </>
          ) : (window.location.pathname.startsWith("/master") &&
              currentMode === "add") ||
            currentMode !== "add" ? (
            <X
              className="sales-form-icons cursor-pointer"
              onClick={() =>
                modeOfEdit === "add" ||
                (window.location.pathname.startsWith("/master") &&
                  currentMode === "add")
                  ? onClose()
                  : handleCloseEditMode()
              }
              size={24}
              title="Close Mode"
            />
          ) : null}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="master-data-modal-form">
        <div
          className={
            heading !== "otherDetailsModal"
              ? "master-data-modal-form-fields"
              : "master-data-modal-field"
          }
        >
          {(() => {
            const normalFields = fields.filter(
              (f) => f.field_type !== "TEXT" && f.field_type !== "text",
            );
            const textFields = fields.filter(
              (f) => f.field_type === "TEXT" || f.field_type === "text",
            );
            return [...normalFields, ...textFields].map((field) =>
              renderField(field),
            );
          })()}
        </div>

        {isPopupMode && !showAllFields && (
          <div className="popup-mode-indicator">
            <strong>Quick Add Mode:</strong> Only required fields are shown. For
            complete details, use the master menu.
          </div>
        )}

        {heading !== "otherDetailsModal" && detailButtons.length > 0 && (
          <div className="add-extra-details">
            <h6>Other Details *</h6>
            <div className="add-detail-buttons">
              {detailButtons.map((text, index) => (
                <button key={index} type="button" className="add-detail-button">
                  {text}
                </button>
              ))}
            </div>
          </div>
        )}

        {!isPopupMode && (
          <div className="additional-actions master-data-modal-form-fields">
            <div className="action-button-grid">
              {/* Upload Drawing */}
              <div className="action-button-wrapper">
                <button
                  type="button"
                  className="action-button upload"
                  onClick={() => drawingInputRef.current?.click()}
                  disabled={!(mode === "add" || currentMode === "edit")}
                >
                  Upload Drawing
                  <Upload size={18} />
                </button>
                <input
                  type="file"
                  ref={drawingInputRef}
                  onChange={(e) =>
                    handleFileUpload(e.target.files[0], "drawing")
                  }
                  style={{ display: "none" }}
                />
                {drawingUrl && (
                  <a
                    href={drawingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-link"
                  >
                    View Drawing
                  </a>
                )}
              </div>

              <div className="action-button-wrapper">
                <button
                  type="button"
                  className="action-button bom"
                  onClick={() => {
                    if (mode !== "add") {
                      navigate(`/${tab}/${category}/bom`, {
                        state: {
                          productData: formData,
                          isViewMode: formData?.bom_created === true,
                        },
                      });
                    }
                  }}
                  disabled={mode !== "view" || currentMode === "edit"}
                >
                  {formData?.bom_created === true ? "BOM" : "Create BOM"}
                  <FilePlus size={18} />
                </button>
              </div>

              <div className="action-button-wrapper">
                <button
                  type="button"
                  onClick={() => {
                    if (mode !== "add") {
                      navigate(`/${tab}/${category}/packing-list`, {
                        state: {
                          productData: formData,
                          isViewMode: formData.pl_created === true,
                        },
                      });
                    }
                  }}
                  className="action-button packaging"
                  disabled={mode !== "view" || currentMode === "edit"}
                >
                  {formData?.pl_created === true
                    ? "Packaging List"
                    : "Add Packaging List"}
                  <Package size={18} />
                </button>
              </div>

              <div className="action-button-wrapper">
                <button
                  type="button"
                  className="action-button qc"
                  onClick={() => qcInputRef.current?.click()}
                  disabled={!(mode === "add" || currentMode === "edit")}
                >
                  QC Inspection Format
                  <ClipboardCheck size={18} />
                </button>
                <input
                  type="file"
                  ref={qcInputRef}
                  onChange={(e) =>
                    handleFileUpload(e.target.files[0], "inspection_format")
                  }
                  style={{ display: "none" }}
                />
                {inspectionFormatUrl && (
                  <a
                    href={inspectionFormatUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-link"
                  >
                    View QC Format
                  </a>
                )}
              </div>

              <div className="action-button-wrapper">
                <button
                  type="button"
                  className="action-button bom"
                  onClick={() => {
                    setShowProcessCardPopup(true);
                    if (mode === "view" && transId) {
                      fetchTransactionData();
                    }
                  }}
                  disabled={mode !== "view" || currentMode === "edit"}
                >
                  {formData?.process_card_created === true
                    ? "View Process Card"
                    : "Create Process Card"}
                  <ClipboardList size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {transactionCode === "PM" &&
          mode === "view" &&
          transId &&
          productBomView &&
          !productBomView.loading && (
            <div className="product-bom-container" style={{ margin: "16px 0" }}>
              <div className="product-bom-header">
                <span className="product-bom-title">
                  BILL OF MATERIALS
                  {productBomView.data?.head_data?.tran_no
                    ? ` \u2014 ${productBomView.data.head_data.tran_no}`
                    : ""}
                </span>
              </div>
              {productBomView.data ? (
                <table className="product-bom-table">
                  <thead>
                    <tr>
                      <th>SL</th>
                      <th>MATERIAL / DESCRIPTION</th>
                      <th>THICKNESS</th>
                      <th>QTY</th>
                      <th>BOM TYPE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(productBomView.data.item_data || []).map(
                      (material, mIndex) => (
                        <tr key={mIndex}>
                          <td>{material.serial_no || mIndex + 1}</td>
                          <td>
                            {material.description ||
                              material.material ||
                              material.product_name ||
                              material.item_name ||
                              "N/A"}
                          </td>
                          <td>
                            {material.thickness ??
                              material.thick_mm ??
                              "\u2014"}
                          </td>
                          <td>
                            {material.total_quantity ??
                              material.quantity ??
                              material.qty ??
                              "\u2014"}
                          </td>
                          <td>
                            {material.bom_type ? (
                              <span className="product-bom-type-chip">
                                {material.bom_type}
                              </span>
                            ) : (
                              "\u2014"
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              ) : (
                <div className="product-bom-empty">No BOM created yet</div>
              )}
            </div>
          )}

        <div className="master-data-modal-footer">
          {isButtonLoading ? (
            <div className="loader-container">
              <span className="loader"></span>
            </div>
          ) : (
            (currentMode === "add" ||
              currentMode === "edit" ||
              modeOfEdit === "add") && (
              <div style={{ display: "flex", gap: "1rem" }}>
                <button
                  type="button"
                  className="master-data-modal-cancel-button"
                  onClick={() =>
                    modeOfEdit === "add" ||
                    (window.location.pathname.startsWith("/master") &&
                      currentMode === "add")
                      ? onClose()
                      : handleCloseEditMode()
                  }
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="master-data-modal-save-button"
                  style={{
                    cursor: (
                      currentMode === "add" ? rights.can_add : rights.can_edit
                    )
                      ? "pointer"
                      : "not-allowed",
                    opacity: (
                      currentMode === "add" ? rights.can_add : rights.can_edit
                    )
                      ? 1
                      : 0.5,
                  }}
                >
                  {finaliseMode ? "Finalise" : "Save"}
                </button>
              </div>
            )
          )}
        </div>
      </form>
      {showProcessCardPopup && (
        <ProcessCardPopup
          processCardNo={formData?.tran_id || ""}
          productName={formData?.name || ""}
          productDescription={formData?.description || ""}
          onClose={() => setShowProcessCardPopup(false)}
          onProcessCardSaved={handleProcessCardSaved}
          process_card_created={formData?.process_card_created === true}
          process_card_id={formData?.process_card_id}
        />
      )}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this transaction?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ProductMaster;
